import type { Draggable } from '@dnd-kit/dom';
import type { DragMoveEvent } from '@dnd-kit/react';
import { DragOverlay, useDragDropManager, useDragDropMonitor } from '@dnd-kit/react';
import { useComputed } from '@dnd-kit/react/hooks';
import { useReducedMotion } from '@words-happen/ui-components';
import type React from 'react';
import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { tileDropAnimation } from './board-drop-animation';
import { BoardTileFace } from './board-tile-face';
import type { Direction, TileData } from './board-types';

/**
 * What every draggable tile registers as its dnd-kit `data`, so one overlay can render a tile dragged
 * from anywhere — board or rack — at the size of wherever it was picked up.
 */
export interface TileDragData {
  /** The tile as its owner knows it, never a presentation copy such as the ghosted origin render. */
  tile: TileData;
  cellSize: number;
  gap: number;
  /** The region holding the tile: `Board`'s `dropId`, or a `TileRack`'s `id`. */
  region: string;
  /** Whether the tile shows its points badge — see `BoardTileFace`. Shown unless `false`. */
  showPoints?: boolean;
}

/** What every region a tile may be dropped into registers as its droppable `data`. */
export interface TileDropData {
  region: string;
}

/**
 * `pointerEvents: none` guards a real regression: dnd-kit keeps the overlay in the top layer for the
 * whole drop animation, sitting directly over the cell the tile just landed on. Left hit-testable it
 * swallows the next pointerdown, so a tile could not be picked up again for ~150ms after being dropped.
 * dnd-kit's own injected stylesheet says the same for the duration of a drag; stating it inline means
 * the guarantee does not depend on that stylesheet having reached this overlay's root. Costs nothing
 * either way — the overlay is aria-hidden decoration that follows the pointer, and the sensor listens
 * on document, so it never needs hits of its own.
 */
const OVERLAY_STYLE: CSSProperties = { pointerEvents: 'none' };

const regionOf = (entity: { data?: { region?: string } } | null | undefined): string | undefined =>
  entity?.data?.region;

/**
 * The tile that follows the pointer during a drag — and the only overlay a `DragDropProvider` may
 * have. dnd-kit holds a single overlay slot per provider, so a second one silently replaces the first;
 * two regions each rendering their own is how a board tile came to vanish mid-drag while the rack's
 * overlay, which knows nothing of board tiles, flew empty in its place.
 *
 * It renders whichever tile is being dragged from the `TileDragData` that tile's draggable carries, so
 * this one component covers `Board` and `TileRack` alike. Both render it themselves when standalone; a
 * screen composing them under a provider of its own renders it once, inside that provider.
 */
export interface TileDragOverlayProps {
  /**
   * A direction to draw the tile in the air with, in place of the one its draggable carries — for a
   * consumer that turns a tile as it hovers somewhere, such as a rack tile over the board it would
   * land turned on. Applies only while the tile being dragged is the one named.
   */
  turned?: { tileId: string; dir: Direction } | null;
}

export const TileDragOverlay: React.FC<TileDragOverlayProps> = ({ turned }) => {
  const reducedMotion = useReducedMotion();
  const manager = useDragDropManager();

  /**
   * The dragged tile's data, read as a signal. `DragOverlay` only re-renders when the *source* it
   * holds changes, and that stays the same draggable for the whole gesture — so a tile turned in the
   * air, which changes the source's `data` and nothing else, would otherwise go on being drawn in the
   * direction it was picked up in. Subscribing to `data` here re-renders this component, and with it
   * the overlay's children.
   */
  const dragData = useComputed(() => manager?.dragOperation.source?.data as TileDragData | undefined, [manager]).value;

  // Whether the tile in the air is hovering over a region other than the one it was picked up from —
  // rack to board, say, rather than a move within either. Tracked from `onDragMove`, which fires
  // continuously well before the drop, so it has settled by drag-end time.
  const [leavingRegion, setLeavingRegion] = useState(false);

  const listeners = useMemo(
    () => ({
      // Reset at the start of the next drag rather than at the end of this one: drag end fires in the
      // same React commit as the app's response to it (moving the tile between regions), and that is
      // exactly the render dnd-kit reads `dropAnimation` from.
      onDragStart: () => setLeavingRegion(false),
      onDragMove: (event: DragMoveEvent) => {
        const { source, target } = event.operation;
        setLeavingRegion(target != null && regionOf(target) !== regionOf(source));
      },
    }),
    [],
  );
  useDragDropMonitor(listeners);

  // The settle animation flies the overlay back to the node dnd-kit measured at pickup. Across a
  // region change that node is gone — the app has just moved the tile into the other region, which
  // mounts a node of its own — so animating would drag the tile visibly back towards where it came
  // from before vanishing. Dropping within one region keeps the node, and with it the animation.
  const dropAnimation = useMemo(
    () => (leavingRegion ? null : tileDropAnimation(reducedMotion)),
    [leavingRegion, reducedMotion],
  );

  return (
    <DragOverlay dropAnimation={dropAnimation} style={OVERLAY_STYLE}>
      {(source: Draggable<TileDragData>) => {
        const data = dragData ?? source.data;
        if (!data?.tile) return null;
        const tile = turned?.tileId === data.tile.id ? { ...data.tile, dir: turned.dir } : data.tile;

        return (
          <BoardTileFace
            aria-hidden="true"
            tile={tile}
            cellSize={data.cellSize}
            gap={data.gap}
            showPoints={data.showPoints}
            lifted
          />
        );
      }}
    </DragOverlay>
  );
};
